'use strict';

(function () {
  const DEFAULT_CONFIG = {
    count: 50,
    size: 2.2,
    velocity: 140,
    fade: false,
  };

  const BURST_POSITIONS = [
    { xRatio: 0.12, yRatio: 0.2 },
    { xRatio: 0.5, yRatio: 0.4 },
    { xRatio: 0.88, yRatio: 0.25 },
  ];

  const BURST_DELAY_MS = 350;
  const BURST_COUNT = 3;
  const SETTLE_DURATION_MS = 1600;
  const FREEZE_DELAY_MS = BURST_DELAY_MS * (BURST_COUNT - 1) + SETTLE_DURATION_MS;

  class Vector2 {
    constructor(x = 0, y = 0) {
      this.x = x;
      this.y = y;
    }

    copy() {
      return new Vector2(this.x, this.y);
    }

    magnitude() {
      return Math.hypot(this.x, this.y);
    }

    normalize() {
      const magnitude = this.magnitude();
      if (magnitude === 0) {
        this.x = 0;
        this.y = 0;
        return this;
      }

      const inverse = 1 / magnitude;
      this.x *= inverse;
      this.y *= inverse;
      return this;
    }

    scale(factor) {
      this.x *= factor;
      this.y *= factor;
      return this;
    }
  }

  function randomRange(min, max) {
    return Math.random() * (max - min) + min;
  }

  class Particle {
    constructor(position, config) {
      this.config = config;
      this.frozen = false;
      this.position = position.copy();
      this.size = this.initSize();
      this.velocity = this.initVelocity();
      this.rotation = randomRange(0, 360);
      this.rotationSpeed = randomRange(-250, 250);
      this.hue = randomRange(0, 360);
      this.opacity = 100;
    }

    initSize() {
      const width = randomRange(2, 10) * this.config.size;
      const height = randomRange(2, 4) * this.config.size;
      return new Vector2(width, height);
    }

    initVelocity() {
      const direction = new Vector2(randomRange(-0.5, 0.5), randomRange(-0.75, 0.25)).normalize();
      direction.x *= Math.random() * this.config.velocity * 4;
      direction.y *= Math.random() * this.config.velocity * 4;
      return direction;
    }

    update(delta) {
      if (this.frozen) {
        return;
      }

      this.velocity.x += randomRange(-350, 350) * delta;
      this.velocity.y += 750 * (this.size.y / (10 * this.config.size)) * delta;

      const drag = Math.pow(0.98, delta * 60);
      this.velocity.scale(drag);

      this.position.x += this.velocity.x * delta;
      this.position.y += this.velocity.y * delta;
      this.rotation += this.rotationSpeed * delta;
    }

    freeze() {
      this.frozen = true;
      this.velocity = new Vector2(0, 0);
      this.rotationSpeed = 0;
    }

    draw(renderer) {
      renderer.drawRect(this.position, this.size, this.rotation, this.hue, this.opacity);
    }
  }

  class BannerRenderer {
    constructor(container, onResize) {
      this.container = container;
      this.onResize = onResize;
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'mix-match-banner__confetti-canvas';

      const context = this.canvas.getContext('2d');
      if (!context) {
        throw new Error('Unable to create 2d context');
      }

      this.context = context;
      this.dpr = Math.max(1, window.devicePixelRatio || 1);
      this.boundResize = this.resize.bind(this);

      container.appendChild(this.canvas);
      this.resizeObserver = new ResizeObserver(this.boundResize);
      this.resizeObserver.observe(container);
      this.resize();
      this.context.imageSmoothingEnabled = true;
    }

    resize() {
      const width = this.container.clientWidth;
      const height = this.container.clientHeight;

      this.canvas.width = Math.round(width * this.dpr);
      this.canvas.height = Math.round(height * this.dpr);
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${height}px`;
      this.context.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

      if (this.onResize) {
        this.onResize();
      }
    }

    get width() {
      return this.canvas.width / this.dpr;
    }

    get height() {
      return this.canvas.height / this.dpr;
    }

    clear() {
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    drawRect(position, size, rotation, hue, opacity) {
      const radians = (rotation * Math.PI) / 180;
      const cos = Math.cos(radians);
      const sin = Math.sin(radians);
      const scaledWidth = size.x * this.dpr;
      const scaledHeight = size.y * this.dpr;
      const x = position.x * this.dpr;
      const y = position.y * this.dpr;

      this.context.setTransform(cos, sin, -sin, cos, x, y);
      this.context.beginPath();
      this.context.rect(-scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
      this.context.fillStyle = `oklch(0.85 0.25 ${hue}deg / ${opacity}%)`;
      this.context.fill();
      this.context.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    }

    cleanup() {
      this.resizeObserver.disconnect();
      this.canvas.remove();
    }
  }

  class ConfettiEngine {
    constructor(container) {
      this.particles = [];
      this.frozen = false;
      this.lastTime = 0;
      this.rafId = 0;
      this.running = false;
      this.boundUpdate = this.update.bind(this);
      this.renderer = new BannerRenderer(container, () => {
        if (this.frozen) {
          this.drawFrame();
        }
      });
    }

    start() {
      if (this.running || this.frozen) {
        return;
      }

      this.running = true;
      this.lastTime = 0;
      this.rafId = window.requestAnimationFrame(this.boundUpdate);
    }

    drawFrame() {
      this.renderer.clear();

      for (let index = 0; index < this.particles.length; index += 1) {
        this.particles[index].draw(this.renderer);
      }
    }

    update(timestamp) {
      if (this.frozen) {
        return;
      }

      const delta = Math.min((timestamp - this.lastTime) / 1000, 0.064);
      this.lastTime = timestamp;

      for (let index = 0; index < this.particles.length; index += 1) {
        this.particles[index].update(delta);
      }

      this.drawFrame();
      this.rafId = window.requestAnimationFrame(this.boundUpdate);
    }

    burst(config) {
      this.start();

      const position = new Vector2(config.position.x, config.position.y);
      for (let index = 0; index < config.count; index += 1) {
        this.particles.push(new Particle(position, config));
      }
    }

    freeze() {
      if (this.frozen) {
        return;
      }

      this.frozen = true;
      this.running = false;

      for (let index = 0; index < this.particles.length; index += 1) {
        this.particles[index].freeze();
      }

      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = 0;
      }

      this.drawFrame();
    }
  }

  function scheduleBursts(container, engine) {
    const width = container.clientWidth;
    const height = container.clientHeight;

    if (width === 0 || height === 0) {
      requestAnimationFrame(() => scheduleBursts(container, engine));
      return;
    }

    BURST_POSITIONS.forEach((position, index) => {
      window.setTimeout(() => {
        engine.burst({
          ...DEFAULT_CONFIG,
          position: {
            x: width * position.xRatio,
            y: height * position.yRatio,
          },
        });
      }, index * BURST_DELAY_MS);
    });

    window.setTimeout(() => {
      engine.freeze();
    }, FREEZE_DELAY_MS);
  }

  function initBanner(banner) {
    const container = banner.querySelector('[data-ref="banner-confetti"]');
    if (!container) {
      return;
    }

    const engine = new ConfettiEngine(container);
    requestAnimationFrame(() => scheduleBursts(container, engine));
  }

  function init() {
    document
      .querySelectorAll('[data-ref="mix-match-banner"][data-show-confetti="true"]')
      .forEach(initBanner);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
